<script setup lang="ts">
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { faEye } from '@fortawesome/duotone-regular-svg-icons'
import useAssignTaskDialog from '~/stores/assignTaskDialog'
import { PendoFeature } from '~/models/Pendo'
import { TaskStatus } from '~/models/AssignTask'
import { useAssignTask } from '~/composables/useAssignTask'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import StudentEditList from '~/components/products/exams/assignment/StudentEditList.vue'
import DeleteTaskDialog from '~/components/products/exams/assignment/DeleteTaskDialog.vue'

const { task } = defineProps<{ task: Task }>()

const { t } = useI18n()
const { setDialogOpen, setDialogClose, isDialogOpen } = useAssignTaskDialog()
const { taskHasBeenClosed } = storeToRefs(useAssignTaskDialog())
const { closeTask: close } = useAssignTask()

const searchQuery = ref('')

const allUsersStatus = computed(() => {
  const allOpen = task.users?.every(user => user.status === TaskStatus.Open)
  const allClosed = task.users?.every(user => user.status === TaskStatus.Closed)
  return allOpen || allClosed
})

const isOpen = computed(() => task.taskId && isDialogOpen(task.taskId, 'edit'))
const openDialog = () => task.taskId && setDialogOpen(task.taskId, 'edit')
const closeDialog = () => task.taskId && setDialogClose(task.taskId, 'edit')

const closeTask = async () => {
  if (!task.taskId)
    return
  await close.mutateAsync(task)
  setDialogOpen(task.taskId, 'assign')
  taskHasBeenClosed.value[task.taskId] = true
}
</script>

<template>
  <AuDialog
    :open="isOpen || false"
    :title="task.name ?? ''"
    size="xl"
    @update:open="val => !val && closeDialog()"
  >
    <template #trigger>
      <Button
        shade="light"
        color="blue"
        @click="openDialog"
      >
        {{ t('assign.editAccess') }}
      </Button>
    </template>
    <template #content>
      <div class="flex flex-col gap-4">
        <div class="border-green-20 bg-green-5 flex size-full gap-2 space-y-1 rounded-md border-2 px-3 py-2 text-green-50">
          <font-awesome-icon
            :icon="faEye"
            class="mt-1.5"
          />
          <div>
            <p
              class="font-semibold"
              v-text="t('assign.taskIsOpen')"
            />
            <p
              class="text-sm font-medium"
              v-text="t('assign.taskStatusInfo')"
            />
          </div>
        </div>
        <StudentEditList
          v-model="searchQuery"
          :task
        />
      </div>
      <div class="mt-4 flex items-center justify-end gap-4">
        <DeleteTaskDialog
          :task
          :disable-btn="close.isPending.value"
        />
        <AuSpinner
          v-if="close.isPending.value"
          size="sm"
        />
        <Button
          v-if="task.status === TaskStatus.Open"
          size="lg"
          :disabled="close.isPending.value"
          :data-pendo="PendoFeature.TermExam.CloseTask"
          color="coral"
          @click="closeTask"
        >
          {{ t('assign.closeForAll') }}
        </Button>
      </div>
    </template>
  </AuDialog>
</template>
